import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { useTranslation } from 'react-i18next'

/**
 * Reusable dashboard charts powered by recharts.
 * Drop into any dashboard that has access to the projects list.
 *
 * Exports:
 *   - ProjectsByStatusPie     — donut chart of project status counts
 *   - BudgetUtilizationBar    — stacked bar of spent vs remaining per project
 *   - InvoiceQueueDonut       — paid vs pending invoice value
 *   - RecommendationsBar      — recommendations by status (consultant)
 */

const STATUS_COLORS = {
  'In Progress': '#3b82f6',
  'Pending Approval': '#f59e0b',
  'Completed': '#10b981',
  'Rejected': '#ef4444',
  'On Hold': '#a78bfa',
  'Pending Closure': '#f97316',
  'Closed': '#6b7280',
  'Archived': '#374151',
  'Planning': '#06b6d4',
  'Draft': '#9ca3af',
}

const PIE_FALLBACK = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#a78bfa', '#6b7280']

// ── Card wrapper for consistent styling ───────────────────────────────────────
function Card({ title, subtitle, children, empty }) {
  return (
    <div className="card">
      <div className="p-5 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="p-5">
        {empty ? (
          <div className="text-center py-8 text-gray-400 text-sm">{empty}</div>
        ) : (
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>{children}</ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}

// ── 1. Projects by Status (donut) ─────────────────────────────────────────────
export function ProjectsByStatusPie({ projects = [] }) {
  const { t } = useTranslation('ui')
  const counts = {}
  for (const p of projects) {
    const s = p.status || 'Unknown'
    counts[s] = (counts[s] || 0) + 1
  }
  const data = Object.entries(counts).map(([name, value]) => ({ name, value }))
  const total = data.reduce((sum, d) => sum + d.value, 0)

  return (
    <Card
      title={t('chart_by_status')}
      subtitle={total ? `${total} total project${total !== 1 ? 's' : ''}` : ''}
      empty={total === 0 ? 'No projects yet — create one to see the breakdown.' : null}
    >
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
          {data.map((entry, i) => (
            <Cell key={i} fill={STATUS_COLORS[entry.name] || PIE_FALLBACK[i % PIE_FALLBACK.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </Card>
  )
}

// ── 2. Budget Utilization per project (stacked bar) ───────────────────────────
export function BudgetUtilizationBar({ projects = [] }) {
  const { t } = useTranslation('ui')
  const data = (projects || [])
    .filter(p => (p.approvedBudget || p.approved_budget || 0) > 0)
    .slice(0, 8) // top 8 to keep readable
    .map(p => ({
      name: (p.name || '').slice(0, 14) + ((p.name || '').length > 14 ? '…' : ''),
      Spent: p.spentBudget || p.spent_budget || 0,
      Remaining: Math.max(0, (p.approvedBudget || p.approved_budget || 0) - (p.spentBudget || p.spent_budget || 0)),
    }))

  return (
    <Card
      title={t('chart_budget')}
      subtitle={t('chart_top8')}
      empty={data.length === 0 ? 'No approved budgets to display.' : null}
    >
      <BarChart data={data} margin={{ top: 5, right: 8, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
        <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="Spent" stackId="a" fill="#3b82f6" />
        <Bar dataKey="Remaining" stackId="a" fill="#d1d5db" />
      </BarChart>
    </Card>
  )
}

// ── 3. Invoice queue paid vs pending (donut, for Finance) ─────────────────────
export function InvoiceQueueDonut({ paidCount = 0, pendingCount = 0, paidValue = 0, pendingValue = 0 }) {
  const { t } = useTranslation('ui')
  const data = [
    { name: 'Paid', value: paidValue || paidCount, count: paidCount },
    { name: 'Pending', value: pendingValue || pendingCount, count: pendingCount },
  ]
  const total = data.reduce((s, d) => s + d.value, 0)
  return (
    <Card
      title={t('chart_invoices')}
      subtitle={`${paidCount + pendingCount} total`}
      empty={total === 0 ? 'No invoices yet — once a milestone is approved, its invoice appears here.' : null}
    >
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85}>
          <Cell fill="#10b981" />
          <Cell fill="#f59e0b" />
        </Pie>
        <Tooltip formatter={(v, n, e) => [`${e.payload.count} invoice(s), $${Number(v).toLocaleString()}`, n]} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </Card>
  )
}

// ── 4. Recommendations by status (bar, for Consultant) ────────────────────────
export function RecommendationsBar({ recommendations = [] }) {
  const { t } = useTranslation('ui')
  const counts = {}
  for (const r of recommendations) {
    const s = r.status || 'Pending'
    counts[s] = (counts[s] || 0) + 1
  }
  const data = Object.entries(counts).map(([name, value]) => ({ name, value }))
  return (
    <Card
      title={t('chart_recommendations')}
      subtitle={`${recommendations.length} total`}
      empty={recommendations.length === 0 ? 'No recommendations submitted yet.' : null}
    >
      <BarChart data={data} margin={{ top: 5, right: 8, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="value" fill="#6366f1" radius={[6, 6, 0, 0]} />
      </BarChart>
    </Card>
  )
}


// ── 5. Priority distribution (bar) ───────────────────────────────────────────
// Was written inline on the PM dashboard only. An Admin overseeing the same projects could not
// see it at all, which is why the PM view looked like the fuller picture of shared data.
export function PriorityDistributionBar({ projects = [] }) {
  const { t } = useTranslation('ui')
  const counts = {}
  for (const p of projects) {
    // Priority is optional, and an unlabelled bar reading "undefined" was one of the things
    // people meant by "the charts do not work".
    const key = p.priority || 'Unset'
    counts[key] = (counts[key] || 0) + 1
  }
  const order = ['High', 'Medium', 'Low', 'Unset']
  const data = Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name))

  return (
    <Card
      title={t('chart_priority')}
      subtitle={`${projects.length} total`}
      empty={data.length === 0 ? 'No projects yet — create one to see the breakdown.' : null}
    >
      <BarChart data={data} margin={{ top: 5, right: 8, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="value" fill="#34d399" radius={[6, 6, 0, 0]} />
      </BarChart>
    </Card>
  )
}


// ── 6. Progress per project (line) ───────────────────────────────────────────
export function ProjectProgressLine({ projects = [] }) {
  const { t } = useTranslation('ui')
  // Finished and shelved projects flatten the line at 0 or 100 and say nothing about the work
  // in hand, so this plots what is still open.
  const data = (projects || [])
    .filter(p => !['Completed', 'Closed', 'Archived', 'Rejected'].includes(p.status))
    .slice(0, 12)
    .map(p => ({
      name: (p.name || '').slice(0, 14) + ((p.name || '').length > 14 ? '…' : ''),
      progress: p.progress || 0,
    }))

  return (
    <Card
      title={t('chart_progress')}
      subtitle={t('chart_progress_sub')}
      empty={data.length === 0 ? 'Nothing open at the moment.' : null}
    >
      <LineChart data={data} margin={{ top: 5, right: 8, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
        <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
        <Tooltip formatter={v => [`${v}%`, t('chart_progress')]} />
        <Line type="monotone" dataKey="progress" stroke="#f59e0b" strokeWidth={3}
          dot={{ fill: '#f59e0b', strokeWidth: 2, r: 5 }} activeDot={{ r: 7 }} />
      </LineChart>
    </Card>
  )
}
